import { useEffect, useState } from 'react';
import {
  IconAngleDoubleLeft,
  IconAngleDoubleRight,
  IconExternalLink,
} from '@iwoca/icons';
import { Button, Spinner } from '@iwoca/orion';
import { Trans } from '@lingui/macro';
import clsx from 'clsx';
import { CopyableText } from 'components/CopyableText/CopyableText';
import { ProgressBarWrapper } from 'components/ProgressBar/ProgressBarWrapper';
import { useRouterApplicationId } from 'components/Signup/hooks/useRouterApplicationId';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { usePathname } from 'hooks/usePathname/usePathname';
import useWindowDimensions from 'hooks/useWindowDimensions';
import {
  TApplicationOverview,
  TIntroducerApplication,
} from 'lendingApi/applications.types';
import {
  useGetIntroducerApplication,
  useGetStateQuery,
} from 'lendingApi/lendingApiHooks';
import { Link } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { BREAK_S } from 'utils/breakpoints';
import {
  APPLICATION_DOCUMENTS_PATH,
  NEW_APPLICATIONS_PATH,
  PATH_NAMES,
} from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './SideNavigation.module.css';

type TProps = {
  documentRequirementsIsFetching?: boolean;
  hideSideNav?: boolean;
  onSaveForLater?: () => void;
  saveForLaterDisabled?: boolean;
  saveForLaterLoading?: boolean;
};

export const SideNavigation = ({
  hideSideNav,
  onSaveForLater,
  saveForLaterDisabled = false,
  saveForLaterLoading = false,
}: TProps) => {
  const pathname = usePathname();
  const fullPathname = usePathname({ fullPath: true });
  const { isUK } = useLocale();
  const { trackEvent } = useAmplitude();
  const { application } = useGetIntroducerApplication();
  const { state, stateStatus, stateFetching } = useGetStateQuery();
  const { width } = useWindowDimensions();
  const { splitTests } = useIntroducerContext();
  const [isNavCollapsed, setIsNavCollapsed] = useState(width <= BREAK_S);

  const [applicationOverview, setApplicationOverview] =
    useState<TApplicationOverview>({
      companyName: '',
      companyNumber: '',
      applicantName: '',
    });

  const stateHasLoaded = state && stateStatus === 'success';
  const { companyName, companyNumber, applicantName } = applicationOverview;
  const applicationId = useRouterApplicationId()!;
  const applicationLink =
    pathname === NEW_APPLICATIONS_PATH && state?.state_key
      ? PATH_NAMES.newApplicationStep1({ applicationId })
      : PATH_NAMES.application({ applicationId });

  const companyInfoUrl = `https://find-and-update.company-information.service.gov.uk/company/${companyNumber}`;

  useEffect(() => {
    if (application) {
      setApplicationOverview({
        companyName: application.company?.registered_company_name,
        companyNumber: application.company?.company_number,
        applicantName: `${application.customer?.first_name} ${application.customer?.last_name}`,
      });
    }
  }, [application, state, stateHasLoaded]);

  useEffect(() => {
    if (stateHasLoaded) {
      const applicant = state.application?.people?.find((person) =>
        person.roles?.includes('applicant'),
      );
      setApplicationOverview({
        companyName: state.application?.company?.registered_company_name,
        companyNumber: state.application?.company?.company_number,
        applicantName: `${applicant?.first_name || ''} ${
          applicant?.last_name || ''
        }`,
      });
    }
  }, [state, stateHasLoaded]);

  const isNewSignup = pathname?.includes(NEW_APPLICATIONS_PATH);
  const applicationStatus = application?.status;

  const eligibleDocumentUploadStatus: TIntroducerApplication['status'][] = [
    'open',
    'action_required',
    'customer_to_link_open_banking',
    'awaiting_underwriter',
    'in_underwriting',
    'manual_review',
  ];

  const showDocumentsTab =
    applicationStatus &&
    eligibleDocumentUploadStatus.includes(applicationStatus);

  const isCollapsable = width <= BREAK_S;

  const handleSaveForLater = () => {
    if (!onSaveForLater) {
      return;
    }

    IwQueryClient.getOrCreate().invalidateQueries({
      queryKey: ['applications'],
    });

    trackEvent(AMPLITUDE_EVENTS.saveAndFinishLaterClicked, {
      'CTA text': 'Save & finish later',
      Reintroduction: Boolean(
        state?.ui?.reintroduction &&
          splitTests?.sameIntroducerAccountReintroductions,
      ),
    });

    onSaveForLater();
  };

  return (
    <aside
      data-testid="side-navigation"
      className={clsx(styles.sideNavigation, {
        [styles.hideSideNavigation]:
          hideSideNav || (isNavCollapsed && isCollapsable),
      })}
    >
      {fullPathname !== PATH_NAMES.newApplicationStep0() &&
        isCollapsable &&
        (isNavCollapsed ? (
          <Button
            data-testid="expand-button"
            variant="denim"
            className={styles.sideNavigationExpand}
            onClick={() => setIsNavCollapsed(false)}
          >
            <IconAngleDoubleRight height={24} width={24} />
          </Button>
        ) : (
          <Button
            data-testid="collapse-button"
            variant="tertiary"
            className={styles.sideNavigationCollapse}
            onClick={() => setIsNavCollapsed(true)}
          >
            <IconAngleDoubleLeft height={24} width={24} />
          </Button>
        ))}
      <section>
        <div className={styles.companyInfo}>
          <div className={styles.companyName}>
            {stateFetching ? (
              <Spinner dataTestId="company-name-spinner" />
            ) : companyName ? (
              <CopyableText
                text={companyName}
                textClassName={styles.detail}
                buttonClassName={styles.copyButton}
                onCopy={() => {
                  trackEvent(AMPLITUDE_EVENTS.copyCompanyNameClicked);
                }}
              />
            ) : (
              <p className={styles.detailBlank}>
                <Trans>(Blank)</Trans>
              </p>
            )}
          </div>

          {isUK && (
            <div className={styles.companyNumber}>
              <h3 className={styles.headers}>
                <Trans>Company number</Trans>
              </h3>
              {stateFetching ? (
                <Spinner dataTestId="company-number-spinner" />
              ) : companyNumber ? (
                <p className={styles.detail}>
                  {companyNumber}
                  {isUK && (
                    <Button
                      data-testid="company-info-external-link"
                      variant="tertiary"
                      iconOnly
                      className={styles.externalLink}
                      href={companyInfoUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() =>
                        trackEvent(AMPLITUDE_EVENTS.externalLinkClicked, {
                          'CTA destination': 'Companies house',
                          'CTA text': '',
                          'CTA URL': companyInfoUrl,
                        })
                      }
                    >
                      <IconExternalLink />
                    </Button>
                  )}
                </p>
              ) : (
                <p className={styles.detailBlank}>
                  <Trans>(Blank)</Trans>
                </p>
              )}
            </div>
          )}
          {/* TODO: Remove temporary condition required for old sign up application with incomplete personal details. */}
          <div>
            <h3 className={styles.headers}>
              <Trans>Applicant</Trans>
            </h3>
            {stateFetching ? (
              <Spinner dataTestId="customer-name-spinner" />
            ) : applicantName?.trim() ? (
              <p data-testid="customer-name" className={styles.detail}>
                {applicantName}
              </p>
            ) : (
              <p className={styles.detailBlank}>
                <Trans>(Blank)</Trans>
              </p>
            )}
          </div>
        </div>
        <ul className={styles.navList}>
          <li className={styles.navListItem}>
            <Link
              to={applicationLink}
              className={clsx(styles.navLink, {
                [styles.navLinkSelected]:
                  pathname === '/application' || isNewSignup,
                [styles.navLinkDisabled]: isNewSignup,
              })}
            >
              <Trans>Application</Trans>
            </Link>
          </li>
          {!isNewSignup && showDocumentsTab && (
            <li className={styles.navListItem}>
              <Link
                to={PATH_NAMES.applicationDocuments({ applicationId })}
                className={clsx(styles.navLink, {
                  [styles.navLinkSelected]:
                    pathname === APPLICATION_DOCUMENTS_PATH,
                })}
              >
                <Trans>Documents</Trans>
              </Link>
            </li>
          )}
        </ul>
      </section>
      {pathname === NEW_APPLICATIONS_PATH && onSaveForLater && (
        <section className={styles.saveForLater}>
          <Button
            data-testid="save-finish-later-button"
            variant="secondary-alt"
            disabled={saveForLaterDisabled}
            loading={saveForLaterLoading}
            onClick={handleSaveForLater}
          >
            <Trans>Save & finish later</Trans>
          </Button>
        </section>
      )}
      {!pathname.includes(NEW_APPLICATIONS_PATH) && (
        <section>
          <ProgressBarWrapper
            className={styles.progressBar}
            status={applicationStatus}
          />
        </section>
      )}
    </aside>
  );
};
